import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import {
	addContact,
	removeContact,
	setContacts,
	updateContact,
} from '../state/contacts.action';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { EMPTY, catchError, firstValueFrom, map, take } from 'rxjs';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { createContact, deleteContact, editContact } from '../graphql/contacts.mutations';
import { contactsQuery } from '../graphql/contacts.queries';
import { ILabel } from '@app/pages/settings/interfaces/labels.interfaces';
import { IContactSet } from '../interfaces/contact.interfaces';
import { TranslateService } from '@tolgee/ngx';
@Injectable({
	providedIn: 'root',
})
export class ContactsService {
	private readonly store$ = inject(Store);
	private readonly apollo = inject(Apollo);
	private readonly notify = inject(IconSnackBarService);
	private readonly translateService = inject(TranslateService);

	contactsQuery(locationId: string = null) {
		return this.apollo
			.query({
				query: contactsQuery,
				fetchPolicy: 'no-cache',
				variables: {
					locationId,
				},
			})
			.pipe(
				take(1),
				map((res: any) => {
					const contacts = res.data.contacts.map(contact => {
						if (contact.location) {
							contact.location = {
								locationId: contact.location.id,
								name: contact.location.name,
								locationType: 'Building',
							};
						}
						//TODO: We can remove this when label issue CMMS-1732 is fixed in backend.
						if (contact.labels) {
							contact.labels = contact.labels.filter(label => label !== null);
						}
						return contact;
					});
					return {
						contacts,
					};
				}),
				catchError(() => {
					this.notify.error('could_not_load_contacts');
					return EMPTY;
				})
			);
	}

	setContacts(contacts: IContactSet[]) {
		this.store$.dispatch(setContacts({ contacts }));
		return;
	}

	handleContacts(
		locationId: string = null,
		loading: WritableSignal<boolean> = signal(true)
	) {
		const contactsQuery$ = this.contactsQuery(locationId);

		firstValueFrom(contactsQuery$).then(contactsRes => {
			loading.set(false);
			this.setContacts(contactsRes.contacts);
		});
	}

	createContact(contactInfo) {
		const {
			company,
			email,
			firstName,
			lastName,
			phoneNumber,
			countryCode,
			labels,
			location,
		} = contactInfo;

		const labelPks = (labels as ILabel[])?.map(label => label.pk);
		return this.apollo
			.mutate({
				mutation: createContact,
				variables: {
					company,
					email,
					firstName,
					lastName,
					locationId: location?.[0].locationId ?? null,
					phoneNumber,
					countryCode,
					labels: labelPks,
				},
			})
			.pipe(
				take(1),
				map((res: any) => {
					this.addContact(res.data.createContact.contact);
				}),
				catchError((error: any) => {
					this.notify.error(
						error?.message ?? this.translateService.instant('could_not_add_contact')
					);
					return EMPTY;
				})
			);
	}

	addContact(contactInfo) {
		if (contactInfo.location) {
			contactInfo.location = {
				...contactInfo.location,
				locationType: 'BUILDING',
				locationId: contactInfo.location?.id,
			};
		}
		this.store$.dispatch(addContact({ contact: contactInfo }));
	}

	updateContact(contactInfo, pk: string) {
		if (contactInfo.location) {
			contactInfo.location = contactInfo.location[0];
		}

		this.store$.dispatch(updateContact({ contact: contactInfo, pk }));
	}

	editContact(contactInfo, pk, removeLocation = false) {
		const {
			company,
			email,
			firstName,
			lastName,
			phoneNumber,
			countryCode,
			labels,
			location,
		} = contactInfo;
		const labelPks = (labels as ILabel[]).map(label => label.pk);
		return this.apollo
			.mutate({
				mutation: editContact,
				variables: {
					pk,
					company,
					countryCode,
					email,
					firstName,
					labels: labelPks,
					lastName,
					locationId: location?.[0]?.locationId ?? null,
					phoneNumber,
					removeLocation,
				},
			})
			.pipe(
				catchError((error: any) => {
					this.notify.error(
						error?.message ?? this.translateService.instant('could_not_edit_contact')
					);
					return EMPTY;
				})
			);
	}

	deleteContact(contactId) {
		return this.apollo
			.mutate({
				mutation: deleteContact,
				fetchPolicy: 'no-cache',
				variables: {
					pk: contactId,
				},
			})
			.pipe(
				take(1),
				catchError(_ => {
					this.notify.error(this.translateService.instant('could_not_delete_contact'));
					return EMPTY;
				})
			);
	}

	removeContact(contactId) {
		this.store$.dispatch(removeContact({ contactId }));
	}
}
